import S from '../Resume/Resume.module.scss';

import React, { useEffect, useState } from 'react';
import ResumeHeadTitle from '../../common/ResumeHeadTitle';
import ResumeTabMenu from '../../common/ResumeTabMenu';
import BasicInfoEdit from './EditMode/BasicInfoEdit';
import EducationEdit from './EditMode/EducationEdit';
import CareerEdit from './EditMode/CareerEdit';
import LanguageEditMode from './EditMode/LanguageSkillEdit';
import AwardEdit from './EditMode/AwardEdit';
import SelfIntroductionEdit from './EditMode/SelfIntroductionEdit';
import EtcEdit from './EditMode/EtcEdit';
import {EducationType, MilitaryServiceInfoType, ProfileType, ResumeType} from '../../../types/ResumeTypes';
import { useRecoilValue } from 'recoil';
import { IvyAuthorizationTokenAtom, IvyMyInfoAtom } from '../../../recoil/Authorization.recoil';
import { IvyUserInfoService } from '../../../service/IvyUserInfoService';
import { fetchResumes, postResume, putResume } from '../../../repository/ResumeRepository';
import _ from 'lodash';
import ExternalActivityEdit from './EditMode/ExternalActivityEdit';
import CertificationEdit from './EditMode/CertificateEdit';
import { searchLanguageQualification } from '../../../repository/EducationRepository';
import Viewer from './Viewer/index.';
import PopupModal from '../../common/PopupModal';
import { DummyResume } from '../../../constants/IvyConstants';
import useMoveScroll from '../../../hooks/useMoveScroll';

interface ResumeProps {}

const Resume = (props: ResumeProps) => {
  // 로그인 인증
  IvyUserInfoService();

  const [isEditMode, setEditMode] = useState<boolean>(false);
  const [resume, setResume] = useState<ResumeType>({} as ResumeType);
  const [editingResume, setEditingResume] = useState<ResumeType>({} as ResumeType);
  const [isResumeExisted, setIsResumeExisted] = useState<boolean>(false);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>('');
  const token = useRecoilValue(IvyAuthorizationTokenAtom);
  const myInfo = useRecoilValue(IvyMyInfoAtom);
  const [activeTab, setActiveTab] = useState(0);

  const resumeTabs = {
    0: useMoveScroll('기본 인적사항', 0, setActiveTab),
    1: useMoveScroll('학력 정보', 1, setActiveTab),
    2: useMoveScroll('경력 정보', 2, setActiveTab),
    3: useMoveScroll('외국어/자격증/수상실적', 3, setActiveTab),
    4: useMoveScroll('자기소개서', 4, setActiveTab),
    5: useMoveScroll('기타', 5, setActiveTab),
    length: 6,
  };

  const getResume = (token: string) => {
    fetchResumes(token)
      .then((res) => {
        if (res && res.length > 0) {
          setIsResumeExisted(true);
          setResume(res[0]); // 1차 배포때는 첫번쨰 요소만 저장
          initEditingResume(res[0]);
        } else {
          setIsResumeExisted(false);
          setResume(DummyResume);
          setEditingResume(DummyResume);
        }
      })
      .catch((e) => console.error(e));
  };

  const editResume = (token: string, id: number, resume: ResumeType) => {
    putResume(token, id, resume)
      .then((res) => {
        if (res) {
          getResume(token);
          setEditMode(false);
        }
      })
      .catch((e) => console.error(e));
  };

  const createResume = (token: string, resume: ResumeType) => {
    postResume(token, resume)
      .then((res) => {
        if (res) {
          getResume(token);
          setEditMode(false);
        }
      })
      .catch((e) => console.error(e));
  };

  const filterEmptyObject = () => {
    const removedEducations = editingResume.educations.filter(
      (item) => !_.isEmpty(item.name) || !_.isEmpty(item.level),
    );
    const removedCareers = editingResume.careers.filter((item) => !_.isEmpty(item.employmentType));
    const removedExternalActivities = editingResume.externalActivities.filter(
      (item) => !_.isEmpty(item.name),
    );
    const removedQualifications = editingResume.qualifications.filter(
      (item) => !_.isEmpty(item.name),
    );
    const removedLanguageSkills = editingResume.languageSkills.filter(
      (item) => !_.isEmpty(item.testName),
    );
    const removedAwards = editingResume.awards.filter((item) => !_.isEmpty(item.name));

    return {
      profile: editingResume.profile,
      educations: removedEducations,
      careers: removedCareers,
      externalActivities: removedExternalActivities,
      qualifications: removedQualifications,
      languageSkills: removedLanguageSkills,
      awards: removedAwards,
      selfIntroduction: editingResume.selfIntroduction,
      militaryServiceInfo: editingResume.militaryServiceInfo,
      veteranStatus: editingResume.veteranStatus,
      veteranRelation: editingResume.veteranRelation,
      disabilityGrade: editingResume.disabilityGrade,
      disabilityStatus: editingResume.disabilityStatus
    } as ResumeType;
  };

  const validateEditingResume = (resume: ResumeType) => {
    const excludedProfileFields = [
      'firstOfPhoneNumber',
      'middleOfPhoneNumber',
      'lastOfPhoneNumber',
      'emailId',
      'emailAddress',
      'profileImageUrl',
      'profileImageUuid',
    ];
    const excludedEducationFields = [
      'academicStatus',
      'grade',
      'standardOfGrade',
      'majors',
      'description',
    ];
    if (isProfileNotValid(resume.profile, excludedProfileFields)) {
      setIsOpenModal(true);
      setModalMessage('프로필 항목중 필수값이 입력되지 않았습니다.');
      return false;
    }

    const isEduFieldNotValid = resume.educations.every((edu) =>
      isEducationNotValid(edu, excludedEducationFields),
    );
    if (isEduFieldNotValid) {
      setModalMessage('학력사항 항목중 필수값이 입력되지 않았습니다.');
      setIsOpenModal(true);
      return false;
    }

    let educationCustomValidation = false;

    resume.educations.forEach((education, index) => {
      if (!_.isEmpty(education['standardOfGrade']) && !_.isEmpty(education['grade'])) {
        if (parseFloat(education['grade']) > parseFloat(education['standardOfGrade'])) {
          setModalMessage('학점 필드는 기준 학점을 넘을 수 없습니다.');
          setIsOpenModal(true);
          educationCustomValidation = true;
          return false;
        }
      }

      if(!_.isEmpty(education['startAt']) && !_.isEmpty(education['endAt'])) {
        const [startYear, startMonth] = education['startAt'].split('-').map(Number);
        const [endYear, endMonth] = education['endAt'].split('-').map(Number);

        if (startYear > endYear) {
          setModalMessage('입학년월은 졸업년월 보다 이후일 수 없습니다.');
          setIsOpenModal(true);
          educationCustomValidation = true;
          return false;
        }
        if (startYear === endYear && startMonth > endMonth) {
          setModalMessage('입학년월은 졸업년월 보다 이후일 수 없습니다.');
          setIsOpenModal(true);
          educationCustomValidation = true;
          return false;
        }
      }
    });

    if (educationCustomValidation) {
      return false;
    }

    const isMilitaryFieldNotValid = _.isEmpty(resume.militaryServiceInfo.status);

    if(isMilitaryFieldNotValid){
      setModalMessage('병역 여부 필드는 필수로 입력되어야 합니다.');
      return false;
    }

    return true;
  };

  const isProfileNotValid = (profile: ProfileType, excludedFields: string[] = []): boolean => {
    const profileFields = Object.keys(profile).filter((field) => !excludedFields.includes(field));
    return profileFields.every((field) => _.isEmpty(profile[field as keyof ProfileType]));
  };

  const isEducationNotValid = (
    education: EducationType,
    excludedFields: string[] = [],
  ): boolean => {
    // empty field check
    const educationFields = Object.keys(education).filter(
      (field) => !excludedFields.includes(field),
    );

    const isSame = educationFields.every((field) => {
      return !_.isEmpty(education[field as keyof EducationType]);
    });
    if (isSame) {
      return _.isEmpty(education['name']);
    } else {
      return true;
    }
  };

  const initEditingResume = (resume: ResumeType) => {
    /*
     * 커스터마이징 해야 될 필드 나눠서 업데이트
     *  */
    // profile
    const editingProfile = { ...resume.profile };
    if (editingProfile) {
      editingProfile.profileImageUuid = editingProfile.profileImageUrl?.uuid;
      const phoneNumber = editingProfile.phoneNumber;
      editingProfile.firstOfPhoneNumber = phoneNumber?.slice(0, 3) || '010';
      editingProfile.middleOfPhoneNumber = phoneNumber?.slice(3, 7) || '';
      editingProfile.lastOfPhoneNumber = phoneNumber?.slice(7, 11) || '';

      const email = editingProfile.email;
      const indexOfWhelk = email.indexOf('@');
      if (indexOfWhelk > 0) {
        editingProfile.emailId = email.slice(0, indexOfWhelk);
        editingProfile.emailAddress = email.slice(indexOfWhelk);
      } else {
        editingProfile.emailId = '';
        editingProfile.emailAddress = '';
      }
    }

    // education
    const editingEducations = [...resume.educations];
    if (editingEducations) {
      editingEducations.map((education) => {
        education.academicStatus =
          _.isEmpty(education.startAt) && _.isEmpty(education.endAt)
            ? ''
            : _.isEmpty(education.endAt)
            ? 'IN_SCHOOL'
            : 'GRADUATION';
      });
    }

    // language-skill
    const editingLanguageSkills = [...resume.languageSkills];
    if (editingLanguageSkills) {
      editingLanguageSkills.map((languageSkill) => {
        if (!_.isEmpty(languageSkill.testName)) {
          searchLanguageQualification(token, languageSkill.testName).then((res) => {
            if (res && res.length > 0) {
              languageSkill.grades = res[0].grades;
            }
          });
        }
      });
    }

    //externalActivity
    // 기획에 따라 달라짐.
    // 초반에 빈 템플릿 노출을 원하면 해당 부분 주석 제거
    // let editingExternalActivities = [...resume.externalActivities];
    // if(editingExternalActivities !== undefined && editingExternalActivities.length === 0){
    //   editingExternalActivities = [DummyExternalActivity];
    // }

    //militaryServiceInfo
    let editingMilitaryServiceInfo = {...resume.militaryServiceInfo};
    if(editingMilitaryServiceInfo === undefined || editingMilitaryServiceInfo.status === undefined) {
      editingMilitaryServiceInfo = {
        status: 'None',
        military: '',
        reason: '',
        startAt: '',
        endAt: ''
      } as MilitaryServiceInfoType;
    }

    setEditingResume((prev) => ({
      ...resume,
      profile: editingProfile,
      educations: editingEducations,
      languageSkills: editingLanguageSkills,
      // externalActivities: editingExternalActivities
      militaryServiceInfo: editingMilitaryServiceInfo
    }));
  };

  useEffect(() => {
    if (myInfo !== undefined && myInfo.id > 0) {
      getResume(token);
    }
  }, [myInfo]);

  if (myInfo === undefined || editingResume === undefined) {
    return null;
  }

  return (
    <>
      <PopupModal
        isOpenModal={isOpenModal}
        setIsOpenModal={setIsOpenModal}
        needsCancelBtn={false}
        message={modalMessage}
      />
      <main className={`${S.resume_container} wrapper`}>
        {/* 등록 전 화면 */}
        {!isEditMode && !isResumeExisted && !isPreview && (
          <>
            <ResumeHeadTitle
              headTitle="이력서"
              resumeType="write"
              resumeHeadAction={() => setEditMode(true)}
            />
            <div className={S.resume_list_wrap}>
              <div
                className={`${S.resume_box_list} wrapper`}
                onClick={() => {
                  setEditMode(true);
                }}
              >
                <button className={`${S.plus_minus_button} ${S.plus_button}`}></button>
              </div>
            </div>
          </>
        )}

        {/* 등록 전 화면 */}
        {isEditMode ? (
          <>
            <ResumeHeadTitle
              headTitle="이력서"
              resumeType="cancel"
              resumeHeadAction={() => setEditMode(false)}
            />
            <ResumeTabMenu resumeTabs={resumeTabs} activeTab={activeTab} />
            <BasicInfoEdit
              profile={editingResume.profile}
              setResume={setEditingResume}
              resumeTabs={resumeTabs}
            />
            <EducationEdit
              educations={editingResume.educations}
              setResume={setEditingResume}
              resumeTabs={resumeTabs}
            />
            <ExternalActivityEdit
              externalActivities={editingResume.externalActivities}
              setResume={setEditingResume}
            />
            <CareerEdit
              careers={editingResume.careers}
              setResume={setEditingResume}
              resumeTabs={resumeTabs}
            />
            <LanguageEditMode
              languageSkills={editingResume.languageSkills}
              setResume={setEditingResume}
              resumeTabs={resumeTabs}
            />
            <CertificationEdit
              certifications={editingResume.qualifications}
              setResume={setEditingResume}
            />
            <AwardEdit awards={editingResume.awards} setResume={setEditingResume} />
            <SelfIntroductionEdit selfIntroduction={editingResume.selfIntroduction} setResume={setEditingResume} resumeTabs={resumeTabs} />
            <EtcEdit resumeTabs={resumeTabs}
                     setResume={setEditingResume}
                     militaryServiceInfo={editingResume.militaryServiceInfo}
                     veteranStatus={editingResume.veteranStatus}
                     veteranRelation={editingResume.veteranRelation}
                     disabilityStatus={editingResume.disabilityStatus}
                     disabilityGrade={editingResume.disabilityGrade}
            />
            <div className={`${S.edit_mode_submit} wrapper`}>
              <button
                type="button"
                className={S.preview_btn}
                onClick={() => {
                  setIsPreview(true);
                  setEditMode(false);
                }}
              >
                <h3 className="h3">미리보기</h3>
              </button>
              <button
                className={S.submit_btn}
                onClick={() => {
                  const filteredResume = filterEmptyObject();
                  if (validateEditingResume(filteredResume)) {
                    if (isResumeExisted) {
                      editResume(token, resume.id, filteredResume);
                    } else {
                      createResume(token, filteredResume);
                    }
                  }
                }}
                type="submit"
              >
                <h3 className="h3">완료</h3>
              </button>
            </div>
          </>
        ) : (
            (isResumeExisted || isPreview) && (
            <Viewer
              isAdmin={false}
              resume={isPreview ? editingResume : resume}
              isPreview={isPreview}
              changeToEditMode={() => {
                setEditMode(true);
                setIsPreview(false);
              }}
            />
          )
        )}
      </main>
    </>
  );
};

export default Resume;
